import React, { useMemo } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import NumberedSection from '../selection/NumberedSection';
import ChoiceCard from '../selection/ChoiceCard';
import SelectableList, { SelectableItem } from '../selection/SelectableList';
import { useFulfillmentContexts } from '../../hooks/useFulfillmentContexts';

export interface FcSelectionValue {
  exportAllFCs: boolean;
  fulfillmentContextIds: string[];
}

export const ALL_FCS: FcSelectionValue = { exportAllFCs: true, fulfillmentContextIds: [] };

interface FulfillmentContextSelectionProps {
  partnerId: string;
  value: FcSelectionValue;
  onChange: (value: FcSelectionValue) => void;
  step?: number;
}

const FulfillmentContextSelection: React.FC<FulfillmentContextSelectionProps> = ({ partnerId, value, onChange, step }) => {
  const { data, loading, error, retry } = useFulfillmentContexts(partnerId);

  const items = useMemo<SelectableItem[]>(
    () => data.map((fc) => ({ id: fc.fulfillmentContextId, primary: fc.name || fc.fulfillmentContextId, secondary: fc.fulfillmentContextId })),
    [data],
  );

  const selectedCount = value.exportAllFCs ? 0 : value.fulfillmentContextIds.length;
  const recordCount = loading ? 'Loading…' : error ? 'Every record for this partner' : `Every ${data.length} records for this partner`;

  return (
    <NumberedSection step={step} title="Which fulfillment contexts" description="Export every fulfillment context for this partner, or pick only the ones you need.">
      <Box role="radiogroup" aria-label="Which fulfillment contexts" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 1.5 }}>
        <ChoiceCard title="All fulfillment contexts" subtitle={recordCount} selected={value.exportAllFCs} onSelect={() => onChange(ALL_FCS)} />
        <ChoiceCard
          title="Choose specific"
          subtitle={value.exportAllFCs ? 'Pick one or more' : `${selectedCount} selected`}
          selected={!value.exportAllFCs}
          onSelect={() => {
            if (value.exportAllFCs) onChange({ exportAllFCs: false, fulfillmentContextIds: [] });
          }}
        />
      </Box>

      {/* The picker is only relevant to "Choose specific"; the list still loads up front for the All card's count. */}
      {value.exportAllFCs ? null : loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6, border: 1, borderColor: 'divider', borderRadius: 3 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading fulfillment contexts…
          </Typography>
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Retry
            </Button>
          }
        >
          Couldn't load fulfillment contexts: {error}. You can still choose All fulfillment contexts.
        </Alert>
      ) : !items.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          This partner has no fulfillment contexts.
        </Typography>
      ) : (
        <SelectableList
          items={items}
          itemNoun="fulfillment contexts"
          selectedIds={value.fulfillmentContextIds}
          onChange={(ids) => onChange({ exportAllFCs: false, fulfillmentContextIds: ids })}
        />
      )}

      {!value.exportAllFCs && selectedCount === 0 && !loading && !error && items.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
          Select at least one fulfillment context, or choose All.
        </Typography>
      )}
    </NumberedSection>
  );
};

export default FulfillmentContextSelection;
